import { Tabs, TabsProps } from 'antd';
import { isDefined } from 'class-validator';
import React, { forwardRef, HTMLAttributes, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import ColumnHighlight from 'src/app/components/ColumnHighlightComponent/ColumnHighlight';
import { ICustomTabs } from 'src/app/components/TabLayoutComponent/tab-layout.config';
import { genericExcludeFilter } from 'src/app/helpers/permission.helper';
import usePermissions from 'src/app/hooks/usePermissions';
import { useAppSelector } from 'src/app/redux/hook';
import { INTERESTS_WHITELIST, LAYOUTVIEW } from 'src/configs/system.config';

export type TabLayoutRef = {
  getMergedTabs: () => TabsProps['items'];
};
interface ITableLayoutProps {
  items: ICustomTabs[];
  className?: 'tabs-with-border-container' | 'dashboard-card-tabs';
  onTabChange?: (value: string) => void;
  customTabOptions?: { tabKey: string; options: HTMLAttributes<HTMLElement> };
  id?: string;
  paramName?: 'tab' | 'subTab';
  outerActiveKey?: string; // To control the active tab from outside
  onActiveTabChange?: (key: string) => void; // To capture active tab change from outside
}

/*
This component is used to create custom tabs for pages.
If no classname is provided, it creates upper tabs.
If 'tabs-with-border-container' is provided as a classname, it creates inner tabs (titles should be buttons).
*/

const TabLayout = forwardRef<TabLayoutRef, ITableLayoutProps>(function TabLayout(
  { items, className, onTabChange, customTabOptions, id, paramName, outerActiveKey, onActiveTabChange },
  ref,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const { customer } = useAppSelector((state) => state.user);
  const { permissions } = usePermissions();

  const [extraContent, setExtraContent] = useState<JSX.Element | string | null>();

  // Apply customer filters for tabs
  const mergedTabs = useMemo(() => {
    return items
      .filter((col) => {
        const resultArr: boolean[] = [];

        if (isDefined(col.permission)) {
          resultArr.push(permissions.indexOf(col.permission) !== -1);
        }

        if (isDefined(col.exclude)) {
          resultArr.push(!col.exclude);
        }

        if (col.excludeAccountTypes) {
          resultArr.push(genericExcludeFilter(col.excludeAccountTypes, customer?.customerAccountTypeId));
        }
        if (isDefined(col.excludeConfigInterest)) {
          resultArr.push(!genericExcludeFilter(INTERESTS_WHITELIST(), col.excludeConfigInterest));
        }
        if (col.excludeLayouts) {
          resultArr.push(genericExcludeFilter(col.excludeLayouts, LAYOUTVIEW()));
        }

        return resultArr.every((val) => val === true);
      })
      .map((col) => {
        const features: ICustomTabs = { key: col.key, label: col.label };
        if (col.tooltipText) {
          features.label = <ColumnHighlight tabs={col} />;
        }
        return {
          ...col,
          ...features,
        };
      });
  }, [items]);

  useImperativeHandle(ref, () => {
    return { getMergedTabs: () => mergedTabs };
  }, [mergedTabs]);

  const getDefaultKey = () => {
    return mergedTabs.length > 0 ? mergedTabs.find((tab) => !tab.disabled)?.key || '1' : '1';
  };

  const getActiveKeyFromUrl = () => {
    if (!paramName) return getDefaultKey();
    const params = new URLSearchParams(location.search);
    const activeKey = params.get(paramName) || getDefaultKey();
    return mergedTabs.some((item) => item.key === activeKey) ? activeKey : getDefaultKey();
  };

  const [activeTabKeyState, setActiveTabKeyState] = useState<string>(getActiveKeyFromUrl);
  // Determine the active tab key to use (either from prop or state)
  const activeTabKeyToUse = outerActiveKey ?? activeTabKeyState;

  // useEffect(() => {
  //   if (onTabChange) {
  //     onTabChange(activeTabKeyToUse);
  //   }
  // }, [activeTabKeyToUse]);

  // Function to update the active tab key either locally or via prop callback
  const setActiveTabKeyLocal = (key: string) => {
    if (onActiveTabChange) {
      onActiveTabChange(key); // Call the prop callback if available
    } else {
      setActiveTabKeyState(key); // Update local state if no prop callback
    }
  };

  // Update the active tab when the URL changes
  useEffect(() => {
    const newActiveKey = getActiveKeyFromUrl();
    if (newActiveKey !== activeTabKeyToUse) {
      setActiveTabKeyLocal(newActiveKey);
    }
  }, [location.search]);

  // Apply extra content for the active tab if provided
  useEffect(() => {
    const getExtraContents = () => {
      const foundTab = items.find((item) => item.key === activeTabKeyToUse);
      if (!foundTab) return;
      setExtraContent(foundTab.extendedContents);
    };
    getExtraContents();
  }, [items, activeTabKeyToUse]);

  const renderTabBars: any = (props: any, DefaultTabProps: any) => {
    return (
      <DefaultTabProps {...props}>
        {(node: any) => {
          if (node.key === customTabOptions?.tabKey) {
            return React.cloneElement(node, {
              ...customTabOptions?.options,
            });
          }
          return node;
        }}
      </DefaultTabProps>
    );
  };

  const onChange = (key: string) => {
    setActiveTabKeyLocal(key); // Update the active tab key
    if (paramName) {
      const params = new URLSearchParams(location.search);
      if (paramName !== 'subTab') {
        // Reset inner tab when the upper tab is changed
        params.set('tab', key);
        params.delete('subTab'); // Reset inner tab
      } else {
        params.set(paramName, key); // Otherwise update inner tab
      }
      navigate({ search: params.toString() }, { state: location.state });
    }

    if (onTabChange) onTabChange(key); // Callback for external changes
  };

  return (
    <div className={className} style={{ height: '100%' }}>
      <Tabs
        id={id}
        style={{ width: '100%' }}
        activeKey={activeTabKeyToUse}
        items={mergedTabs}
        onChange={onChange}
        tabBarExtraContent={<div style={{ paddingInlineEnd: '1rem' }}> {extraContent} </div>}
        destroyInactiveTabPane
        renderTabBar={customTabOptions ? renderTabBars : undefined}
      />
    </div>
  );
});

export default TabLayout;
